import { toAbsoluteUrl } from "@/navigation/absoluteUrl";
import type { AppLocationCodec } from "@/navigation/appLocation";

export type ScreenerResourceType = "portfolio" | "product" | "index";

export type ScreenerLocation = {
  configId?: string;
  resourceId?: string;
  resourceType?: ScreenerResourceType;
};

export const SCREENER_OS_PARAM_KEYS = ["configId", "resourceId", "resourceType"] as const;

const SCREENER_RESOURCE_TYPES = new Set<ScreenerResourceType>(["portfolio", "product", "index"]);

function parseResourceType(raw: string | null | undefined): ScreenerResourceType | undefined {
  if (raw && SCREENER_RESOURCE_TYPES.has(raw as ScreenerResourceType)) {
    return raw as ScreenerResourceType;
  }
  return undefined;
}

export function normalizeScreener(loc: ScreenerLocation): ScreenerLocation {
  const next: ScreenerLocation = {};
  const configId = loc.configId?.trim();
  if (configId) next.configId = configId;
  const resourceId = loc.resourceId?.trim();
  if (resourceId) next.resourceId = resourceId;
  if (loc.resourceType) next.resourceType = loc.resourceType;
  return next;
}

export function parseScreenerFromSearchParams(searchParams: URLSearchParams): ScreenerLocation {
  const loc: ScreenerLocation = {};
  const configId = searchParams.get("configId")?.trim();
  if (configId) loc.configId = configId;
  const resourceId = searchParams.get("resourceId")?.trim();
  if (resourceId) loc.resourceId = resourceId;
  const resourceType = parseResourceType(searchParams.get("resourceType"));
  if (resourceType) loc.resourceType = resourceType;
  return normalizeScreener(loc);
}

export function parseScreenerFromOsParams(params: Record<string, string>): ScreenerLocation {
  const loc: ScreenerLocation = {};
  const configId = params.configId?.trim();
  if (configId) loc.configId = configId;
  const resourceId = params.resourceId?.trim();
  if (resourceId) loc.resourceId = resourceId;
  const resourceType = parseResourceType(params.resourceType);
  if (resourceType) loc.resourceType = resourceType;
  return normalizeScreener(loc);
}

export function serializeScreenerToSearchParams(loc: ScreenerLocation): URLSearchParams {
  const normalized = normalizeScreener(loc);
  const params = new URLSearchParams();
  if (normalized.configId) params.set("configId", normalized.configId);
  if (normalized.resourceId) params.set("resourceId", normalized.resourceId);
  if (normalized.resourceType) params.set("resourceType", normalized.resourceType);
  return params;
}

export function serializeScreenerToOsParams(loc: ScreenerLocation): Record<string, string> {
  const out: Record<string, string> = {};
  serializeScreenerToSearchParams(loc).forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

export function buildScreenerWindowUrl(loc: ScreenerLocation): string {
  const qs = serializeScreenerToSearchParams(loc).toString();
  return `/screener${qs ? `?${qs}` : ""}`;
}

/** Absolute share URL for the screener (same shape as `buildScreenerWindowUrl`). */
export function screenerShareUrl(loc: ScreenerLocation): string {
  return toAbsoluteUrl(buildScreenerWindowUrl(loc));
}

export const screenerCodec: AppLocationCodec<ScreenerLocation> = {
  appId: "screener",
  fromUrl: (_pathname, searchParams) => parseScreenerFromSearchParams(searchParams),
  fromParams: (params) => parseScreenerFromOsParams(params),
  toParams: (loc) => serializeScreenerToOsParams(loc),
  toUrl: (loc) => buildScreenerWindowUrl(loc),
};

export function screenerLocationsEqual(a: ScreenerLocation, b: ScreenerLocation): boolean {
  const na = normalizeScreener(a);
  const nb = normalizeScreener(b);
  return (
    (na.configId ?? "") === (nb.configId ?? "") &&
    (na.resourceId ?? "") === (nb.resourceId ?? "") &&
    (na.resourceType ?? "") === (nb.resourceType ?? "")
  );
}
